import { useState, useEffect, useRef } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Settings, 
  Save, 
  Building2, 
  CreditCard,
  Loader2,
  Upload,
  FileText,
  Trash2,
  TestTube,
  AlertTriangle,
  Bell,
  Mail
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InvoiceSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [settings, setSettings] = useState({
    annual_amount: 200,
    entry_fee: 0,
    entry_fee_enabled: false,
    currency: 'CHF',
    club_name: 'Swiss Historic Racing Team',
    club_address: '',
    club_zip: '',
    club_city: '',
    club_country: 'CH',
    bank_name: '',
    iban: '',
    bic: '',
    payment_reference: '',
    invoice_prefix: 'SHRT',
    payment_days: 30,
    reminder_days: 14,
    auto_reminders: false,
    payment_slip_filename: null,
    test_mode: true,
    transaction_notification_email: '',
    transaction_notifications_enabled: false,
    payment_method_card: true,
    payment_method_twint: true,
    payment_method_cash: true
  });

  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const token = getToken();
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/invoices/settings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Merge with defaults so new fields get proper initial values
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const token = getToken();

    try {
      await axios.put(
        `${API_URL}/api/admin/invoices/settings`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Nur PDF-Dateien erlaubt');
      return;
    }
    
    setUploading(true);
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/invoices/settings/payment-slip`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      setSettings(prev => ({ ...prev, payment_slip_filename: response.data.filename }));
      toast.success('Einzahlungsschein hochgeladen');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePaymentSlip = async () => {
    const token = getToken();
    
    try {
      await axios.delete(
        `${API_URL}/api/admin/invoices/settings/payment-slip`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings(prev => ({ ...prev, payment_slip_filename: null }));
      toast.success('Einzahlungsschein gelöscht');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Settings className="text-primary" size={28} />
          <h1 className="font-['Oswald'] text-2xl font-bold text-foreground uppercase tracking-tight">
            Rechnungseinstellungen
          </h1>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Betrag */}
          <div className="bg-card border border-border p-6">
            <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
              <CreditCard size={16} />
              Mitgliedsbeitrag
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Jahresbeitrag
                </Label>
                <Input
                  type="number"
                  value={settings.annual_amount}
                  onChange={(e) => handleChange('annual_amount', parseFloat(e.target.value))}
                  className="bg-secondary/50 border-border rounded-none"
                  data-testid="annual-amount"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Währung
                </Label>
                <Input
                  value={settings.currency}
                  onChange={(e) => handleChange('currency', e.target.value)}
                  className="bg-secondary/50 border-border rounded-none"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Rechnungspräfix
                </Label>
                <Input
                  value={settings.invoice_prefix}
                  onChange={(e) => handleChange('invoice_prefix', e.target.value)}
                  className="bg-secondary/50 border-border rounded-none"
                />
              </div>
            </div>
          </div>

          {/* Eintrittsgebühr */}
          <div className="bg-card border border-border p-6">
            <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
              <CreditCard size={16} />
              Eintrittsgebühr
            </h2>
            <p className="text-xs text-muted-foreground mb-4">
              Wird einmalig bei Neumitgliedern im ersten Jahr als separate Position auf der Rechnung ausgewiesen.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Eintrittsgebühr
                </Label>
                <Input
                  type="number"
                  value={settings.entry_fee}
                  onChange={(e) => handleChange('entry_fee', parseFloat(e.target.value) || 0)}
                  className="bg-secondary/50 border-border rounded-none"
                  data-testid="entry-fee"
                />
              </div>
              <div className="space-y-2 flex items-end">
                <label className="flex items-center gap-2 cursor-pointer h-10">
                  <input
                    type="checkbox"
                    checked={settings.entry_fee_enabled}
                    onChange={(e) => handleChange('entry_fee_enabled', e.target.checked)}
                    className="w-4 h-4 accent-primary"
                    data-testid="entry-fee-enabled"
                  />
                  <span className="text-sm">Eintrittsgebühr aktiviert</span>
                </label>
              </div>
            </div>
          </div>

          {/* Vereinsdaten */}
          <div className="bg-card border border-border p-6">
            <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
              <Building2 size={16} />
              Vereinsdaten
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Vereinsname
                </Label>
                <Input
                  value={settings.club_name}
                  onChange={(e) => handleChange('club_name', e.target.value)}
                  className="bg-secondary/50 border-border rounded-none"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Adresse
                </Label>
                <Input
                  value={settings.club_address}
                  onChange={(e) => handleChange('club_address', e.target.value)}
                  className="bg-secondary/50 border-border rounded-none"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  PLZ
                </Label>
                <Input
                  value={settings.club_zip}
                  onChange={(e) => handleChange('club_zip', e.target.value)}
                  className="bg-secondary/50 border-border rounded-none"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Ort
                </Label>
                <Input
                  value={settings.club_city}
                  onChange={(e) => handleChange('club_city', e.target.value)}
                  className="bg-secondary/50 border-border rounded-none"
                />
              </div>
            </div>
          </div>

          {/* Bankverbindung */}
          <div className="bg-card border border-border p-6">
            <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
              <CreditCard size={16} />
              Bankverbindung
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Bank
                </Label>
                <Input
                  value={settings.bank_name}
                  onChange={(e) => handleChange('bank_name', e.target.value)}
                  className="bg-secondary/50 border-border rounded-none"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  IBAN
                </Label>
                <Input
                  value={settings.iban}
                  onChange={(e) => handleChange('iban', e.target.value)}
                  placeholder="CH00 0000 0000 0000 0000 0"
                  className="bg-secondary/50 border-border rounded-none"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  BIC/SWIFT
                </Label>
                <Input
                  value={settings.bic}
                  onChange={(e) => handleChange('bic', e.target.value)}
                  className="bg-secondary/50 border-border rounded-none"
                />
              </div>
            </div>
          </div>

          {/* Zahlungsfristen */}
          <div className="bg-card border border-border p-6">
            <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-4">
              Zahlungsfristen & Mahnungen
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Zahlungsfrist (Tage)
                </Label>
                <Input
                  type="number"
                  value={settings.payment_days}
                  onChange={(e) => handleChange('payment_days', parseInt(e.target.value))}
                  className="bg-secondary/50 border-border rounded-none"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Mahnintervall (Tage)
                </Label>
                <Input
                  type="number"
                  value={settings.reminder_days}
                  onChange={(e) => handleChange('reminder_days', parseInt(e.target.value))}
                  className="bg-secondary/50 border-border rounded-none"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Automatische Mahnungen
                </Label>
                <select
                  value={settings.auto_reminders ? 'true' : 'false'}
                  onChange={(e) => handleChange('auto_reminders', e.target.value === 'true')}
                  className="w-full h-10 px-3 bg-secondary/50 border border-border rounded-none text-foreground"
                >
                  <option value="false">Deaktiviert</option>
                  <option value="true">Aktiviert</option>
                </select>
              </div>
            </div>
          </div>

          {/* Einzahlungsschein */}
          <div className="bg-card border border-border p-6">
            <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
              <FileText size={16} />
              Einzahlungsschein (2. Seite)
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Laden Sie hier eine PDF-Datei mit dem Einzahlungsschein hoch. Diese wird als zweite Seite an jede Rechnung angehängt.
            </p>
            
            <div className="flex items-center gap-4">
              {settings.payment_slip_filename ? (
                <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/30 flex-1">
                  <FileText className="text-green-500" size={20} />
                  <span className="text-sm text-foreground flex-1">
                    {settings.payment_slip_filename}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleDeletePaymentSlip}
                    className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-secondary/30 border border-border flex-1">
                  <FileText className="text-muted-foreground" size={20} />
                  <span className="text-sm text-muted-foreground">
                    Kein Einzahlungsschein hochgeladen
                  </span>
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileUpload}
                className="hidden"
                id="payment-slip-upload"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="rounded-none"
              >
                {uploading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="mr-2 h-4 w-4" />
                )}
                PDF hochladen
              </Button>
            </div>
          </div>

          {/* Transaktionsbenachrichtigungen */}
          <div className="bg-card border border-border p-6">
            <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
              <Bell size={16} />
              Transaktionsbenachrichtigungen
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1 flex-1">
                  <p className="text-sm text-foreground">
                    E-Mail bei jeder Transaktion
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Bei jeder Zahlung und Rückerstattung wird eine Benachrichtigung gesendet.
                  </p>
                </div>
                <Switch
                  checked={settings.transaction_notifications_enabled}
                  onCheckedChange={(checked) => handleChange('transaction_notifications_enabled', checked)}
                  data-testid="transaction-notifications-switch"
                />
              </div>
              
              {settings.transaction_notifications_enabled && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                    <Mail size={12} />
                    Benachrichtigungs-E-Mail
                  </Label>
                  <Input
                    type="email"
                    value={settings.transaction_notification_email || ''}
                    onChange={(e) => handleChange('transaction_notification_email', e.target.value)}
                    placeholder="admin@shrt.ch"
                    className="bg-secondary/50 border-border rounded-none"
                  />
                  <p className="text-xs text-muted-foreground">
                    Diese Adresse erhält bei jeder Zahlung und Rückerstattung eine E-Mail.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Zahlungsmethoden */}
          <div className="bg-card border border-border p-6">
            <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
              <CreditCard size={16} />
              Online-Zahlungsmethoden
            </h2>
            <p className="text-xs text-muted-foreground mb-4">
              Wählen Sie, welche Zahlungsmethoden bei Stripe-Checkout angeboten werden. Deaktivierte Methoden werden den Mitgliedern nicht angezeigt.
            </p>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1 flex-1">
                  <p className="text-sm text-foreground">Kreditkarte</p>
                  <p className="text-xs text-muted-foreground">Visa, Mastercard, American Express</p>
                </div>
                <Switch
                  checked={settings.payment_method_card !== false}
                  onCheckedChange={(checked) => handleChange('payment_method_card', checked)}
                  data-testid="payment-method-card-switch"
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-1 flex-1">
                  <p className="text-sm text-foreground">TWINT</p>
                  <p className="text-xs text-muted-foreground">Schweizer Mobile-Payment (muss in Stripe aktiviert sein)</p>
                </div>
                <Switch
                  checked={settings.payment_method_twint !== false}
                  onCheckedChange={(checked) => handleChange('payment_method_twint', checked)}
                  data-testid="payment-method-twint-switch"
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-1 flex-1">
                  <p className="text-sm text-foreground">Barzahlung vor Ort</p>
                  <p className="text-xs text-muted-foreground">Option bei Event-Anmeldungen anzeigen</p>
                </div>
                <Switch
                  checked={settings.payment_method_cash !== false}
                  onCheckedChange={(checked) => handleChange('payment_method_cash', checked)}
                  data-testid="payment-method-cash-switch"
                />
              </div>
              {settings.payment_method_card === false && settings.payment_method_twint === false && (
                <div className="flex items-center gap-2 p-3 bg-yellow-500/20 border border-yellow-500/30 text-yellow-200">
                  <AlertTriangle size={16} className="text-yellow-500 flex-shrink-0" />
                  <span className="text-sm">
                    Mindestens eine Online-Zahlungsmethode sollte aktiv sein, damit Mitglieder online bezahlen können.
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Test-Modus */}
          <div className={`border p-6 ${settings.test_mode ? 'bg-yellow-500/10 border-yellow-500/50' : 'bg-card border-border'}`}>
            <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
              <TestTube size={16} />
              Test-Modus
            </h2>
            
            <div className="flex items-center justify-between">
              <div className="space-y-1 flex-1">
                <p className="text-sm text-foreground">
                  Alle Rechnungs-E-Mails an Admin senden
                </p>
                <p className="text-xs text-muted-foreground">
                  Im Test-Modus werden alle E-Mails an die Admin-Adresse gesendet, anstatt an die Mitglieder.
                </p>
              </div>
              <Switch
                checked={settings.test_mode}
                onCheckedChange={(checked) => handleChange('test_mode', checked)}
                data-testid="test-mode-switch"
              />
            </div>
            
            {settings.test_mode && (
              <div className="mt-4 flex items-center gap-2 p-3 bg-yellow-500/20 border border-yellow-500/30 text-yellow-200">
                <AlertTriangle size={16} className="text-yellow-500 flex-shrink-0" />
                <span className="text-sm">
                  <strong>Achtung:</strong> Der Test-Modus ist aktiv. Alle Rechnungs-E-Mails werden nur an den Admin gesendet.
                </span>
              </div>
            )}
          </div>

          <Button
            type="submit"
            disabled={saving}
            className="bg-primary hover:bg-red-700 text-white uppercase tracking-widest font-bold text-sm rounded-none btn-glow"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Einstellungen speichern
          </Button>
        </form>
      </div>
    </Layout>
  );
}
